import { ConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import React, { useCallback, useContext, useMemo } from 'react';
import { EditorContext } from 'studio/pages/Editor/Editor';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { syncPolicies } from '../Synchronization/Synchronization';
import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useCheckboxStyles, useListItemIconStyles, useListItemTextStyles } from './SyncModal.styles';
import services from 'services';
import { partition } from 'utils/arrayUtils';
import { useMessage } from 'studio/components/Message';

/** Sync modal in studio that allows user to select concepts to synchronize  */
const SyncModal = () => {
  const {
    kgId,
    fileState: { openFilePaths },
    fileOperations: { reloadFileContents, getFiles },
    sync: {
      showSyncDialog,
      selectedSyncDialogSynchronizableConcepts,
      setSelectedSyncDialogSynchronizableConcepts,
      setShowSyncDialog,
      syncDialogSynchronizableConcepts
    },
    refs: { conceptTreeRef }
  } = useContext(EditorContext);

  const { successMessage, errorMessage, warningMessage } = useMessage();

  const listItemClasses = useListItemTextStyles();
  const checkboxClasses = useCheckboxStyles();
  const listItemIconClasses = useListItemIconStyles();

  const synchronize = useCallback(
    async conceptsToSynchronize => {
      const synchronizables = conceptsToSynchronize.map(concept => ({ uri: concept.data.uri }));
      // create the synchronization
      const result = await services.createSynchronization(kgId, synchronizables);
      if (result.success) {
        const synchronization = result.data;
        if (synchronization.data.status === 'success') {
          const synchronizableResults = synchronization.data.result.synchronizableResults.filter(
            synchronizable => synchronizable.syncPolicy !== syncPolicies.ignore
          );
          const [successfulSynchronizableResults, failedSynchronizableResults] = partition(
            synchronizableResults,
            synchronizableResult => synchronizableResult.isSuccess
          );
          Promise.all([reloadFileContents(...openFilePaths), getFiles()]).then(() => {
            if (successfulSynchronizableResults.length > 0) {
              const identifiers = successfulSynchronizableResults.flatMap(synchronizableResult =>
                conceptsToSynchronize
                  .filter(concept => concept.data.uri === synchronizableResult.uri)
                  .map(concept => concept.data.vklQualifiedIdentifier)
              );
              successMessage('Successfully synchronized ' + identifiers.join());
            }
            failedSynchronizableResults.forEach(synchronizableResult =>
              warningMessage(synchronizableResult.errorMessage)
            );
          });
        } else {
          if (synchronization?.data?.result?.synchronizableResults?.length > 0) {
            synchronization.data.result.synchronizableResults.forEach(synchronizableResult => {
              if (!synchronizableResult.isSuccess) {
                errorMessage(synchronizableResult.errorMessage);
              }
            });
          } else {
            errorMessage(synchronization.data.result.errorMessage || 'There was an error synchronizing the entities');
          }
        }
      } else {
        const message = result?.response?.data?.data?.message || 'There was an error synchronizing the entities';
        errorMessage(message);
      }

      // reload the tree items of the synchronized classes in the concept tree: this is necessary
      // in order for synchronized classes to then show the button to open the entities files
      const classUris = conceptsToSynchronize
        .filter(concept => concept.type === 'Class')
        .map(concept => concept.data.uri);
      conceptTreeRef.current.reloadClassTreeItems(classUris);
    },
    [kgId, successMessage, errorMessage, reloadFileContents, openFilePaths, getFiles, conceptTreeRef]
  );

  const syncModalContent = useMemo(() => {
    if (syncDialogSynchronizableConcepts && selectedSyncDialogSynchronizableConcepts) {
      const classList = (
        <List>
          {syncDialogSynchronizableConcepts.map(concept => {
            const isSelected = selectedSyncDialogSynchronizableConcepts.includes(concept);
            return (
              <ListItem key={concept.data.uri} disablePadding>
                <ListItemButton
                  dense
                  disabled={concept.data.syncPolicy === syncPolicies.ignore}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedSyncDialogSynchronizableConcepts(
                        selectedSyncDialogSynchronizableConcepts.filter(selectedConcept => selectedConcept !== concept)
                      );
                    } else {
                      setSelectedSyncDialogSynchronizableConcepts([
                        ...selectedSyncDialogSynchronizableConcepts,
                        concept
                      ]);
                    }
                  }}
                >
                  <ListItemIcon classes={listItemIconClasses}>
                    <Checkbox
                      classes={checkboxClasses}
                      edge="start"
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      checked={isSelected}
                    />
                  </ListItemIcon>
                  <ListItemText classes={listItemClasses} primary={concept.data.vklQualifiedIdentifier} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      );
      return (
        <React.Fragment>
          Choose the classes to synchronize. Note that this can take a long time and lead to downtime for large classes.
          {classList}
        </React.Fragment>
      );
    }
  }, [selectedSyncDialogSynchronizableConcepts, syncDialogSynchronizableConcepts]);

  //  Need to use ConfirmationModal manually here because we use state for the content.
  return (
    <ConfirmationModal
      open={showSyncDialog}
      title="Synchronize entities"
      onConfirm={async () => {
        await synchronize(selectedSyncDialogSynchronizableConcepts);
        setSelectedSyncDialogSynchronizableConcepts(undefined);
        setShowSyncDialog(false);
      }}
      onClose={() => {
        setSelectedSyncDialogSynchronizableConcepts(undefined);
        setShowSyncDialog(false);
      }}
      confirmButtonText="Synchronize"
      disableConfirm={selectedSyncDialogSynchronizableConcepts && selectedSyncDialogSynchronizableConcepts.length === 0}
    >
      {syncModalContent}
    </ConfirmationModal>
  );
};

export default SyncModal;
